import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from "react";
import { AuthContext } from "@/components/AuthProvider";
import { urlInstanceSlug } from "@/lib/named-org";
import { AppHeaderBar } from "@/components/ui/app-header";
import Link from "@/components/DashboardLink";
import { BarChart3, Bell, Box, Github, House, SettingsIcon } from "lucide-react";
import { isCodesmithPath, isHomePath, isProofPath, isSandboxesPath, isStandaloneAccountPath } from "./sidebar/useActiveApp";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { SessionSkeleton } from "./codesmith/SessionSkeleton";
import { BlacksmithLogomarkAnimationSVG, BlacksmithLogomarkSVG } from "./BlacksmithLogoSVG";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE } from "./sidebar/SidebarGlobalChrome";
import { readCachedSidebarVariant } from "./codesmith/sidebarVariant";
import { RailStatusWidgetSkeleton } from "./sidebar/StatusIndicator";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./sidebar/railConstants";
import { DesktopLayoutSkeleton } from "./desktop/DesktopLayoutSkeleton";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { getCachedCodesmithEnabled, getCachedLastActiveOrg, getCachedSandboxesEnabled } from "./sidebar/useCodesmithEnabledCache";

// This ensures the skeleton matches the actual sidebar state even during initial render
export function getSidebarStateFromStorage(): boolean {
  if (typeof window === "undefined") {
    return true; // Default to expanded on SSR
  }
  try {
    const stored = localStorage.getItem("sidebar-open");
    if (stored === null) {
      return true; // Default to expanded if not set
    }
    return stored === "true";
  } catch {
    return true; // Default to expanded on error
  }
}

/**
 * Twin of the mounted rail's nav row: same 32px footprint, icon box, and
 * resting icon color. With an href it is a live Link with the rail's hover
 * and focus affordances, so a click landing during the skeleton window
 * navigates instead of dying on placeholder chrome; without one it stays a
 * plain div that shouldn't advertise interactivity.
 */
function RailSkeletonRow(t0) {
  const $ = _c(16);
  const {
    icon,
    active,
    slot,
    href,
    label
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contents = t1;
  if (href) {
    const t2 = active && RAIL_ROW_ACTIVE;
    let t3;
    if ($[2] !== t2) {
      t3 = cn(RAIL_ROW, t2);
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== contents || $[5] !== href || $[6] !== label || $[7] !== slot || $[8] !== t3) {
      t4 = <Link href={href} aria-label={label} data-slot={slot} className={t3}>{contents}</Link>;
      $[4] = contents;
      $[5] = href;
      $[6] = label;
      $[7] = slot;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  const t2 = active && RAIL_ROW_ACTIVE;
  let t3;
  if ($[10] !== t2) {
    t3 = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md", "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400", t2);
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== contents || $[13] !== slot || $[14] !== t3) {
    t4 = <div data-slot={slot} className={t3}>{contents}</div>;
    $[12] = contents;
    $[13] = slot;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}

/**
 * Placeholder matching AppSwitcherRail's slim footprint: logo, org-switcher
 * pill, app rows, and the global footer (three icon rows + status widget), so
 * the rail never sits as a blank strip while user/org data loads. Every fixed
 * glyph is the mounted rail's own icon rather than a gray block: none of them
 * depend on the data being awaited, so a placeholder there would only flash
 * before the identical icon lands. Only the org pill, whose content is the
 * data being loaded, stays a `Skeleton`; the status widget is a static twin at
 * its own pending values (see RailStatusWidgetSkeleton), which keeps the footer
 * rows above it at their final height. The Codesmith and Proof app rows follow
 * the same gate the mounted rail uses, so nothing pops in or out on swap;
 * so does the Sandboxes row, which shows only on a cached true flag.
 */
export function RailSkeleton() {
  const $ = _c(57);
  const router = useRouter();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const isCodesmithRoute = isCodesmithPath(router.pathname);
  const {
    user
  } = useContext(AuthContext);
  let showCodesmithRow;
  let t0;
  if ($[0] !== urlOrg || $[1] !== user?.default_instance_slug) {
    const instanceSlug = urlInstanceSlug(undefined, user?.default_instance_slug);
    showCodesmithRow = getCachedCodesmithEnabled(urlOrg, instanceSlug) !== false;
    t0 = getCachedSandboxesEnabled(urlOrg, instanceSlug);
    $[0] = urlOrg;
    $[1] = user?.default_instance_slug;
    $[2] = showCodesmithRow;
    $[3] = t0;
  } else {
    showCodesmithRow = $[2];
    t0 = $[3];
  }
  const showSandboxesRow = t0 === true;
  const t1 = urlOrg ?? null;
  let t2;
  if ($[4] !== t1) {
    t2 = readCachedSidebarVariant(t1);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const redesign = t2 === "redesign";
  let t3;
  if ($[6] !== urlOrg) {
    t3 = urlOrg || getCachedLastActiveOrg();
    $[6] = urlOrg;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const navOrg = t3;
  const activeSection = router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : isCodesmithRoute ? "codesmith" : isProofPath(router.pathname) ? "proof" : isSandboxesPath(router.pathname) ? "sandboxes" : "actions";
  const t4 = redesign && RAIL_REDESIGN_CLASS;
  let t5;
  if ($[8] !== t4) {
    t5 = cn("h-full shrink-0 bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  let t7;
  if ($[10] !== t6) {
    t7 = {
      width: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AppHeaderBar><div className="flex size-8 shrink-0 items-center justify-center"><div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div></div></AppHeaderBar>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = redesign ? undefined : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Skeleton className="h-8 w-full rounded-lg" />;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = !redesign && "gap-1 pt-1";
  let t13;
  if ($[16] !== t12) {
    t13 = cn("rail-rows flex flex-col", t12);
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <House className="size-[18px]" />;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = activeSection === "home";
  const t16 = navOrg ? `/${navOrg}/home` : undefined;
  let t17;
  if ($[19] !== t15 || $[20] !== t16) {
    t17 = <RailSkeletonRow icon={t14} active={t15} label="Home" href={t16} />;
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Github className="size-[18px]" />;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const t19 = activeSection === "actions";
  const t20 = navOrg ? `/${navOrg}/runs/jobs` : undefined;
  let t21;
  if ($[23] !== t19 || $[24] !== t20) {
    t21 = <RailSkeletonRow icon={t18} active={t19} label="GitHub Actions" href={t20} />;
    $[23] = t19;
    $[24] = t20;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== activeSection || $[27] !== navOrg || $[28] !== showCodesmithRow) {
    t22 = showCodesmithRow && <RailSkeletonRow icon={<CodesmithIcon />} active={activeSection === "codesmith"} label="[code]smith" slot="rail-skeleton-codesmith-row" href={navOrg ? `/${navOrg}/sessions/new` : undefined} />;
    $[26] = activeSection;
    $[27] = navOrg;
    $[28] = showCodesmithRow;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== activeSection || $[31] !== navOrg || $[32] !== showCodesmithRow) {
    t23 = showCodesmithRow && <RailSkeletonRow icon={<ProofIconBold className="size-[18px]" />} active={activeSection === "proof"} label="Proof" href={navOrg ? `/${navOrg}/proof` : undefined} />;
    $[30] = activeSection;
    $[31] = navOrg;
    $[32] = showCodesmithRow;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== activeSection || $[35] !== navOrg || $[36] !== showSandboxesRow) {
    t24 = showSandboxesRow && <RailSkeletonRow icon={<Box className="size-[18px]" />} active={activeSection === "sandboxes"} label="Sandboxes" slot="rail-skeleton-sandboxes-row" href={navOrg ? `/${navOrg}/sandboxes` : undefined} />;
    $[34] = activeSection;
    $[35] = navOrg;
    $[36] = showSandboxesRow;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== t13 || $[39] !== t17 || $[40] !== t21 || $[41] !== t22 || $[42] !== t23 || $[43] !== t24) {
    t25 = <div className={t13}>{t17}{t21}{t22}{t23}{t24}</div>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="flex-1" />;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== activeSection || $[47] !== redesign) {
    t27 = redesign ? <div className="rail-rows flex flex-col pb-2"><RailSkeletonRow icon={<BarChart3 className="size-[18px]" />} active={activeSection === "usage"} /><RailSkeletonRow icon={<Bell className="size-[18px]" />} /><RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} /><RailStatusWidgetSkeleton /></div> : <div className="rail-rows flex flex-col gap-1 pb-2"><RailSkeletonRow icon={<BarChart3 className="size-[18px]" />} active={activeSection === "usage"} /><RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} /><RailSkeletonRow icon={<Bell className="size-[18px]" />} /><div className="pt-1"><RailStatusWidgetSkeleton /></div></div>;
    $[46] = activeSection;
    $[47] = redesign;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== t11 || $[50] !== t25 || $[51] !== t27) {
    t28 = <div className="flex h-full w-full flex-col px-2">{t8}{t11}{t25}{t26}{t27}</div>;
    $[49] = t11;
    $[50] = t25;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t28 || $[54] !== t5 || $[55] !== t7) {
    t29 = <div className={t5} style={t7} data-slot="app-switcher-rail-skeleton">{t28}</div>;
    $[53] = t28;
    $[54] = t5;
    $[55] = t7;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  return t29;
}

/**
 * Full-page loading skeleton: rail + sidebar skeleton + centered logo in main
 * area, or rail + SessionSkeleton on codesmith routes (which have no Actions
 * sidebar; the session sidebar lives inside the main pane at a persisted width).
 * Used during auth checks and pending-auth query failures so loading matches app chrome.
 * AuthProvider only renders this on the client after hydration, so
 * localStorage can be read synchronously on first paint.
 */
export function SidebarLayoutSkeleton() {
  const $ = _c(19);
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const [sidebarOpen] = useState(getSidebarStateFromStorage);
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCodesmithRoute = t0;
  let t1;
  if ($[2] !== router.pathname) {
    t1 = isStandaloneAccountPath(router.pathname);
    $[2] = router.pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isStandalone = t1;
  if (surface === "desktop") {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <DesktopLayoutSkeleton />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RailSkeleton />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isCodesmithRoute || $[7] !== isStandalone || $[8] !== router.pathname || $[9] !== sidebarOpen) {
    t3 = !isCodesmithRoute && !isHomePath(router.pathname) && !isProofPath(router.pathname) && !isSandboxesPath(router.pathname) && <SidebarSkeleton collapsed={!sidebarOpen} standalone={isStandalone} />;
    $[6] = isCodesmithRoute;
    $[7] = isStandalone;
    $[8] = router.pathname;
    $[9] = sidebarOpen;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== isCodesmithRoute) {
    t4 = <SidebarInset className="flex-1 bg-transparent! overflow-y-auto [scrollbar-gutter:stable]">{isCodesmithRoute ? <SessionSkeleton /> : <div className="flex h-full items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>}</SidebarInset>;
    $[11] = isCodesmithRoute;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex-1 flex overflow-hidden">{t2}{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== sidebarOpen || $[17] !== t5) {
    t6 = <div className="flex flex-col h-dvh"><SidebarProvider open={sidebarOpen} className="flex-1 min-h-0">{t5}</SidebarProvider></div>;
    $[16] = sidebarOpen;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}